import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useLocation, useRemoveStaticSkeleton } from '@common/hooks';
import { fullstorySetUser } from 'works/legacy/lib/fullstory';
import { bootIntercom, updateIntercom } from 'works/legacy/lib/intercom';
import { FeatureFlagsProvider } from '@common/contexts';
import { MomentShadowFlagSync } from '@common/services/moment-shadow';
import { ErrorHandler } from '@common/components';
import { redirectToLogin } from 'works/legacy/lib/redirect-to-login';
import Auth from 'works/legacy/lib/auth';
import { CurrentMembershipContextQuery } from '@identity/graphql';
import {
  CurrentMembershipContext,
  CurrentEntityContext,
} from '@identity/contexts';
import { CurrentEntityContextQuery } from '@institutions/graphql';

function isPublicRoute(path) {
  // If updated, also change the PUBLIC_ROUTES in the welcome_controller.rb file.
  const PUBLIC_ROUTES_KEYS = [
    '/works/sign_in',
    '/works/auth/forgot-password',
    '/works/auth/reset-password/',
    '/works/auth/activate-account/',
    '/works/supplier/signup/',
  ];

  return PUBLIC_ROUTES_KEYS.some((authRouteKey) =>
    path?.includes(authRouteKey)
  );
}

// Entity failed to load: render the error and tear down the boot skeleton,
// which otherwise stays up (it paints above the fold) and hides the error.
function EntityErrorScreen({ error }) {
  useRemoveStaticSkeleton();

  return <ErrorHandler error={error} log="AuthGuard:CurrentEntity" />;
}

EntityErrorScreen.propTypes = {
  error: PropTypes.object,
};

export function AuthGuard({ children }) {
  const location = useLocation();
  const [isPublicPath, setIsPublicPath] = useState(
    isPublicRoute(location.pathname)
  );

  const membershipQuery = useQuery(CurrentMembershipContextQuery, {
    skip: isPublicPath,
  });

  const entityQuery = useQuery(CurrentEntityContextQuery, {
    skip: isPublicPath,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (membershipQuery.data?.currentMembership) {
      Auth.update({
        saveLastVisitedPage:
          membershipQuery.data.currentMembership.saveLastVisitedPage,
      });
    }
  }, [membershipQuery.data]);

  useEffect(() => {
    updateIntercom();
    setIsPublicPath(isPublicRoute(location.pathname));
  }, [location]);

  if (isPublicPath) return null;

  if (membershipQuery.loading || entityQuery.loading) return null;

  if (membershipQuery.error || !membershipQuery.data?.currentMembership) {
    redirectToLogin();
    return null;
  }

  if (entityQuery.error || !entityQuery.data?.currentEntity) {
    return <EntityErrorScreen error={entityQuery.error} />;
  }

  const worksMembership = membershipQuery.data.currentMembership;
  const worksUser = worksMembership.worksUser;
  const entity = worksMembership.entity;

  fullstorySetUser(worksUser, worksMembership);

  Auth.update({
    logo: entity.logoAvatarUrl || '',
    entityName: entity.name || '',
    name: worksMembership.name || '',
    membershipRole: worksMembership.roleName,
  });

  bootIntercom({
    user_id: worksUser.id,
    email: worksUser.email,
    name: `${worksUser.firstName} ${worksUser.lastName}`.trim(),
    first_name: worksUser.firstName,
    last_name: worksUser.lastName,
    membership_role: worksMembership.roleName,
    active: worksMembership.active,
    company: {
      company_id: entity.id,
      name: entity.name,
    },
    user_hash: worksUser.intercomUserHash,
  });

  return (
    <CurrentMembershipContext.Provider value={worksMembership}>
      <CurrentEntityContext.Provider value={entityQuery.data.currentEntity}>
        <FeatureFlagsProvider>
          <MomentShadowFlagSync />
          {children}
        </FeatureFlagsProvider>
      </CurrentEntityContext.Provider>
    </CurrentMembershipContext.Provider>
  );
}

AuthGuard.propTypes = {
  children: PropTypes.node,
};
